/**
 * SubscriptionGate — FAIL-CLOSED subscription wrapper.
 *
 * ACCESS RULES (fail-closed):
 *   - Access is ONLY granted when the backend explicitly returns access_allowed=true,
 *     OR when RevenueCat confirms an active entitlement (trusted StoreKit check).
 *   - Timeouts, network errors, malformed responses, and exceptions
 *     result in an ERROR state with retry — NEVER automatic access.
 *   - Non-iOS platforms pass through (web/Android gating handled by ProtectedRoute).
 *
 * ROLE AWARENESS:
 *   - Owners without access see the paywall (ChoosePlanScreen)
 *   - Drivers without access see a "contact your administrator" message
 *
 * CANCELLED SUBSCRIPTIONS:
 *   - Backend's computeAccessAllowed handles cancelled-with-future-renewal correctly.
 *   - Expired subscriptions return access_allowed=false → paywall/contact_admin.
 *
 * POST-PURCHASE:
 *   - handleActivated is called ONLY after a verified purchase/restore with
 *     confirmed entitlement. The 30s grace window prevents re-gating.
 */

import { useEffect, useState, useRef, useCallback, ReactNode, memo } from "react";
import { Loader2, ShieldAlert, WifiOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isIOSNative, hasActiveEntitlement, initRevenueCat, getSubscriptionStatus } from "@/services/revenuecat";
import { ChoosePlanScreen } from "./ChoosePlanScreen";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { logger } from "@/lib/logger";
import { getCachedSubscription, setCachedSubscription } from "@/lib/subscriptionCache";
import { isActiveWorkflow } from "@/lib/activeWorkflowGuard";

interface Props {
  children: ReactNode;
  onNoWebSubscription?: () => void;
}

type GateStatus = "loading" | "allowed" | "paywall" | "contact_admin" | "error";

/** Max time to wait for backend before showing error (not granting access). */
const TIMEOUT_MS = 15_000;
const RETRY_DELAYS_MS = [250, 500, 1000];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getErrorMessage = (error: unknown): string => {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error !== null && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
};

/**
 * invokeSubscriptionDecision — calls the backend edge function with retries.
 * Returns { data, error } similar to Supabase SDK convention.
 */
async function invokeSubscriptionDecision(): Promise<{
  data: any | null;
  error: string | null;
}> {
  let lastError: string | null = null;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) {
      await wait(RETRY_DELAYS_MS[attempt - 1]);
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        "get-my-subscription"
      );

      if (error) {
        lastError = getErrorMessage(error);
        logger.warn(`[SubGate] Backend attempt ${attempt + 1} error:`, lastError);
        continue;
      }

      return { data, error: null };
    } catch (e) {
      lastError = getErrorMessage(e);
      logger.warn(`[SubGate] Backend attempt ${attempt + 1} exception:`, lastError);
    }
  }

  return { data: null, error: lastError || "All retry attempts failed" };
}

function SubscriptionGateInner({ children, onNoWebSubscription }: Props) {
  const { status: authStatus, session } = useAuth();
  const userId = session?.user?.id ?? null;

  // Refs to prevent double-resolution and track state across renders
  const hasResolvedRef = useRef(false);
  const resolvedStateRef = useRef<GateStatus | null>(null);
  const lastUserIdRef = useRef<string | null>(null);
  const roleRef = useRef<string>("owner");

  /** Grace period after a verified purchase — prevents re-gating during backend sync lag */
  const manualUnlockUntilRef = useRef<number>(0);

  // ── SYNC cache check at init: skip loading screen entirely on iOS WebView restart ──
  // Try userId from session first, fall back to extracting from localStorage auth token.
  const [status, setStatus] = useState<GateStatus>(() => {
    if (hasResolvedRef.current && resolvedStateRef.current) {
      return resolvedStateRef.current;
    }
    // Resolve userId: prefer session, fall back to cached auth token
    let resolvedUserId = userId;
    if (!resolvedUserId) {
      try {
        const raw = localStorage.getItem("sb-wfsbisepapnnnqyyekip-auth-token");
        if (raw) {
          const parsed = JSON.parse(raw);
          resolvedUserId = parsed?.user?.id ?? null;
        }
      } catch {}
    }
    // Check localStorage cache synchronously — if valid, start as "allowed"
    // so the driver NEVER sees the "Checking subscription…" flash.
    if (resolvedUserId) {
      const cached = getCachedSubscription(resolvedUserId);
      if (cached && cached.accessAllowed) {
        logger.debug("[SubGate] Sync cache hit at init — skipping loading screen");
        hasResolvedRef.current = true;
        resolvedStateRef.current = "allowed";
        roleRef.current = cached.role;
        lastUserIdRef.current = resolvedUserId;
        return "allowed";
      }
    }
    // WORKFLOW GUARD: If there's an active workflow and stored tokens exist,
    // start as "allowed" to prevent blocking the driver while verification happens.
    if (isActiveWorkflow()) {
      const hasTokens = (() => {
        try {
          const raw = localStorage.getItem("sb-wfsbisepapnnnqyyekip-auth-token");
          return !!raw && raw.includes("refresh_token");
        } catch { return false; }
      })();
      if (hasTokens) {
        logger.warn("[SubGate] Active workflow + stored tokens — starting as 'allowed' (will verify in background)");
        hasResolvedRef.current = true;
        resolvedStateRef.current = "allowed";
        return "allowed";
      }
    }
    return "loading";
  });

  logger.debug("[SubGate] status:", status, "auth:", authStatus);

  const resolveOnce = useCallback((next: GateStatus, reason: string) => {
    // During manual unlock grace period, do not re-gate to paywall/contact_admin/error
    if (hasResolvedRef.current && manualUnlockUntilRef.current > Date.now() && next !== "allowed") {
      logger.debug(`[SubGate] Skipping re-gate to ${next} — grace period active`);
      return;
    }
    // WORKFLOW GUARD: If an active appraisal is in progress, do NOT gate
    // to error/contact_admin/paywall — only allow transitions to "allowed".
    if (next !== "allowed" && isActiveWorkflow()) {
      logger.warn(`[SubGate] Blocking gate to ${next} — active workflow detected (${reason})`);
      return;
    }
    // DRIVER GUARD: Drivers on job/dashboard routes should never be blocked.
    // Their admin's subscription state can be transitional — let them work.
    if (next !== "allowed" && roleRef.current === "driver") {
      const path = window.location.pathname;
      if (path.startsWith("/driver") || path.startsWith("/resume-job") || path.startsWith("/job/") || path === "/dashboard" || path === "/") {
        logger.warn(`[SubGate] Blocking gate to ${next} for driver on job route: ${path} (${reason})`);
        return;
      }
    }
    if (hasResolvedRef.current && next === resolvedStateRef.current) {
      return;
    }
    hasResolvedRef.current = true;
    resolvedStateRef.current = next;
    logger.debug(`[SubGate] → ${next} (${reason})`);
    setStatus(next);
  }, []);

  const runCheck = useCallback(async (cancelled: { current: boolean }) => {
    try {
      // ── Check subscription cache first (prevents re-verification on iOS WebView reloads) ──
      if (userId) {
        const cached = getCachedSubscription(userId);
        if (cached && cached.accessAllowed) {
          logger.debug("[SubGate] Using cached subscription (age:", Date.now() - cached.cachedAt, "ms)");
          roleRef.current = cached.role;
          resolveOnce("allowed", "cached verification");
          return;
        }
      }

      logger.debug("[SubGate] Checking backend subscription…");

      const { data, error } = await invokeSubscriptionDecision();
      if (cancelled.current) return;

      // FAIL-CLOSED: Any backend error → show error state, NOT access
      if (error) {
        logger.error("[SubGate] Backend error — DENYING access");
        resolveOnce("error", `backend error: ${error}`);
        return;
      }

      // FAIL-CLOSED: Malformed response → show error state, NOT access
      if (!data || typeof data.access_allowed !== "boolean") {
        logger.error("[SubGate] Malformed backend response — DENYING access");
        resolveOnce("error", "malformed subscription response");
        return;
      }

      logger.debug("[SubGate] Backend response — allowed:", data.access_allowed, "status:", data.status);

      const role = data.role ?? "owner";
      roleRef.current = role;
      const accessAllowed = data.access_allowed === true;

      if (accessAllowed) {
        // Cache for iOS WebView reload resilience
        if (userId) {
          setCachedSubscription(userId, {
            accessAllowed: true,
            tier: data.tier ?? null,
            jobLimit: data.job_limit ?? null,
            aiEnabled: data.ai_enabled ?? false,
            status: data.status ?? null,
            provider: data.provider ?? null,
            renewalDate: data.renewal_date ?? null,
            graceAccess: data.grace_access ?? false,
            accessSource: data.access_source ?? "none",
            role,
          });
        }
        resolveOnce("allowed", `access_allowed=true (status: ${data.status})`);
      } else if (role === "driver") {
        // Drivers cannot purchase — show "contact administrator"
        resolveOnce("contact_admin", `driver without access`);
      } else {
        // Owner without backend access — check RevenueCat as trusted entitlement source
        logger.debug("[SubGate] Backend denied, checking RevenueCat…");
        try {
          await initRevenueCat();
          const rcActive = await hasActiveEntitlement();
          if (rcActive) {
            logger.debug("[SubGate] RevenueCat confirms active entitlement");
            // Trigger backend sync so next check won't need RC fallback
            try {
              const subscriptionStatus = await getSubscriptionStatus();
              const { data: { user: currentUser } } = await supabase.auth.getUser();
              if (currentUser && subscriptionStatus.productId) {
                supabase.functions.invoke("sync-apple-subscription", {
                  body: {
                    product_id: subscriptionStatus.productId,
                    is_active: subscriptionStatus.isActive,
                    expiration_date: subscriptionStatus.expirationDate,
                    is_sandbox: subscriptionStatus.isSandbox,
                    will_renew: subscriptionStatus.willRenew,
                    period_type: subscriptionStatus.periodType,
                  },
                }).then(() => logger.debug("[SubGate] Backend sync triggered"))
                  .catch((syncError) => logger.warn("[SubGate] Backend sync failed:", syncError));
              }
            } catch (syncErr) {
              logger.warn("[SubGate] Sync attempt failed:", syncErr);
            }
            resolveOnce("allowed", "RevenueCat verified active entitlement");
            return;
          }
        } catch (rcErr) {
          // RevenueCat check failed — do NOT grant access, fall through to paywall
          logger.warn("[SubGate] RevenueCat check failed");
        }
        // No verified entitlement anywhere — show paywall
        resolveOnce("paywall", `access_allowed=false (status: ${data.status || "none"})`);
      }
    } catch (err) {
      if (cancelled.current) return;
      // FAIL-CLOSED: Exception → show error state, NOT access
      logger.error("[SubGate] Exception — DENYING access:", getErrorMessage(err));
      resolveOnce("error", `exception: ${getErrorMessage(err)}`);
    }
  }, [resolveOnce, userId]);

  const recheckAccess = useCallback(() => {
    logger.debug("[SubGate] Recheck requested");
    hasResolvedRef.current = false;
    resolvedStateRef.current = null;
    setStatus("loading");
    const cancelled = { current: false };
    void runCheck(cancelled);
  }, [runCheck]);

  useEffect(() => {
    logger.debug("[SubGate] Effect — auth:", authStatus);

    if (authStatus !== "authed" || !userId) {
      logger.debug("[SubGate] Waiting for authenticated session");
      return;
    }

    if (hasResolvedRef.current && lastUserIdRef.current === userId && resolvedStateRef.current === "allowed") {
      logger.debug("[SubGate] Already resolved for same user");
      return;
    }

    if (lastUserIdRef.current !== null && lastUserIdRef.current !== userId) {
      logger.debug("[SubGate] User changed, resetting");
      hasResolvedRef.current = false;
      resolvedStateRef.current = null;
      setStatus("loading");
    }

    lastUserIdRef.current = userId;

    // Non-iOS: subscription enforcement handled by ProtectedRoute
    if (!isIOSNative()) {
      resolveOnce("allowed", "non-iOS passthrough");
      return;
    }

    const cancelled = { current: false };

    // FAIL-CLOSED: Timeout shows error state, NOT access
    const timer = setTimeout(() => {
      if (!cancelled.current && !hasResolvedRef.current) {
        logger.error("[SubGate] Timeout after 15s — showing error (FAIL-CLOSED)");
        resolveOnce("error", "timeout — subscription check took too long");
      }
    }, TIMEOUT_MS);

    void runCheck(cancelled);

    return () => {
      cancelled.current = true;
      clearTimeout(timer);
    };
  }, [authStatus, userId, resolveOnce, runCheck]);

  useEffect(() => {
    if (!isIOSNative() || authStatus !== "authed" || !userId) return;
    if (status !== "contact_admin" && status !== "error") return;

    const timer = setTimeout(() => {
      logger.debug(`[SubGate] Auto-retrying access check from ${status}`);
      recheckAccess();
    }, 10_000);

    return () => clearTimeout(timer);
  }, [authStatus, userId, status, recheckAccess]);

  /**
   * handleActivated — called ONLY after a verified purchase or restore.
   * Sets a 30s grace window during which re-gating is forbidden.
   */
  const handleActivated = useCallback(() => {
    logger.debug("[SubGate] handleActivated — verified purchase/restore");
    manualUnlockUntilRef.current = Date.now() + 30_000;
    hasResolvedRef.current = true;
    resolvedStateRef.current = "allowed";
    setStatus("allowed");
  }, []);

  /**
   * handleRetry — resets state and re-runs the subscription check.
   */
  const handleRetry = useCallback(() => {
    recheckAccess();
  }, [recheckAccess]);

  // ── Loading state ──
  // WORKFLOW GUARD: During active appraisals, never block with loading spinner.
  // The driver is mid-photo — show content and verify in background.
  if (status === "loading") {
    if (isActiveWorkflow()) {
      logger.warn("[SubGate] Loading but active workflow — rendering content immediately");
      return <>{children}</>;
    }
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Checking subscription…</p>
      </div>
    );
  }

  // ── Error state (FAIL-CLOSED: verification failed, no access granted) ──
  // WORKFLOW GUARD: During active appraisals, render content despite verification failure
  if (status === "error") {
    if (isActiveWorkflow()) {
      logger.warn("[SubGate] Error state but active workflow — rendering content");
      return <>{children}</>;
    }
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <WifiOff className="h-6 w-6 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold">Verification Failed</h2>
            <p className="text-sm text-muted-foreground">
              We couldn't verify your subscription. Please check your connection and try again.
            </p>
            <Button onClick={handleRetry} className="w-full">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Driver without inherited access ──
  if (status === "contact_admin") {
    if (isActiveWorkflow()) {
      logger.warn("[SubGate] Contact admin state but active workflow — rendering content");
      return <>{children}</>;
    }
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
              <ShieldAlert className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold">Subscription Required</h2>
            <p className="text-sm text-muted-foreground">
              Your subscription is managed by your company administrator. Please contact your administrator if you need access to additional features.
            </p>
            <Button onClick={handleRetry} variant="outline" className="w-full">
              Retry access
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Owner without access — show paywall ──
  if (status === "paywall") {
    return <ChoosePlanScreen onSubscriptionActivated={handleActivated} />;
  }

  // ── Verified access — render children ──
  return <>{children}</>;
}

export const SubscriptionGate = memo(SubscriptionGateInner);
